import { useRef, useEffect, useCallback } from 'react';
import { SdkErrorResponse } from 'src/utilities/notifications';
import { IErrorResponse } from 'src/utilities/shared-types';

export type PollingError = IErrorResponse | SdkErrorResponse;

export interface UsePollingOptions {
  pollingIntervalMs: number;
  maxAttempts: number;
  onError?: (error: PollingError) => void;
}

export interface UsePollingReturn<T> {
  startPolling: () => Promise<T | undefined>;
  stopPolling: () => void;
}

export function usePolling<T>(
  fetchCallback: () => Promise<T>,
  shouldContinuePolling: (response: T) => boolean,
  options: UsePollingOptions
): UsePollingReturn<T> {
  const { pollingIntervalMs, maxAttempts, onError } = options;

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const retriesRef = useRef(0);
  const isMountedRef = useRef(true);

  /**
   * Generation-based cancellation ID.
   * Incremented on every 'startPolling' to invalidate stale in-flight asynchronous requests.
   */
  const abortRef = useRef(0);

  /**
   * Stops the active timer, preventing the NEXT scheduled poll from executing.
   * Note: This does NOT stop an asynchronous fetch that is already in progress.
   */
  const clearPolling = useCallback((): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return (): void => {
      isMountedRef.current = false;
      clearPolling();
    };
  }, [clearPolling]);

  const handlePollError = useCallback(
    (error: PollingError, isInitialCall: boolean): boolean => {
      clearPolling();
      if (!isInitialCall && onError && isMountedRef.current) {
        onError(error);
      }
      return isInitialCall;
    },
    [clearPolling, onError]
  );

  const executePoll = useCallback(
    async (isInitialCall = false): Promise<T | undefined> => {
      // Capture the current generation at the start of this async execution.
      const generation = abortRef.current;

      const scheduleNextPollIfNeeded = (result: T): void => {
        if (shouldContinuePolling(result) && retriesRef.current < maxAttempts) {
          retriesRef.current += 1;
          timeoutRef.current = setTimeout((): void => {
            executePoll(false);
          }, pollingIntervalMs);
        } else {
          clearPolling();
        }
      };

      try {
        if (!isMountedRef.current) return undefined;

        const result = await fetchCallback();

        /**
         * RACE CONDITION PROTECTION:
         * We verify that:
         * 1. The component is still mounted.
         * 2. 'startPolling' hasn't been called again (abortRef.current === generation).
         *
         * If abortRef.current !== generation, it means a newer polling cycle was started
         * while this fetch was in flight. We must ignore this stale result and avoid
         * scheduling new timers.
         */
        if (!isMountedRef.current || abortRef.current !== generation) {
          return undefined;
        }

        scheduleNextPollIfNeeded(result);
        return result;
      } catch (error) {
        if (handlePollError(error as PollingError, isInitialCall)) throw error;
        return undefined;
      }
    },
    [
      fetchCallback,
      shouldContinuePolling,
      maxAttempts,
      pollingIntervalMs,
      clearPolling,
      handlePollError,
    ]
  );

  /**
   * Starts a new polling lifecycle.
   * Increments the generation ID to effectively cancel any previous in-flight requests.
   */
  const startPolling = useCallback(async (): Promise<T | undefined> => {
    abortRef.current += 1;
    clearPolling();
    retriesRef.current = 1;
    return executePoll(true);
  }, [clearPolling, executePoll]);

  return { startPolling, stopPolling: clearPolling };
}
